import React, { useMemo } from 'react';

interface PhaseGaugeProps {
  phase: 'R' | 'S' | 'T';
  power: number;       // 實功率 W
  voltage: number;     // 電壓 V
  current: number;     // 電流 A
  maxPower?: number;   // 最大功率（用於計算指針角度）
  size?: number;       // 尺寸，預設 130px
}

/**
 * 三相電錶指針式儀表盤組件
 * 半圓形設計，指針顯示功率，底部顯示電壓/電流
 */
const PhaseGauge: React.FC<PhaseGaugeProps> = ({
  phase,
  power: rawPower,
  voltage: rawVoltage,
  current: rawCurrent,
  maxPower = 10000,
  size = 130,
}) => {
  // Ensure values are numbers
  const power = typeof rawPower === 'number' ? rawPower : Number(rawPower) || 0;
  const voltage = typeof rawVoltage === 'number' ? rawVoltage : Number(rawVoltage) || 0;
  const current = typeof rawCurrent === 'number' ? rawCurrent : Number(rawCurrent) || 0;
  // 相位顏色配置
  const phaseConfig = {
    R: { 
      primary: '#dc2626',    // red-600
      light: '#fecaca',      // red-200
      label: 'R 相'
    },
    S: { 
      primary: '#ca8a04',    // yellow-600
      light: '#fef08a',      // yellow-200
      label: 'S 相'
    },
    T: { 
      primary: '#2563eb',    // blue-600
      light: '#bfdbfe',      // blue-200
      label: 'T 相'
    },
  };

  const config = phaseConfig[phase];

  // 計算角度 (-90° 到 +90°，共 180° 半圓)
  const START_ANGLE = -90;
  const END_ANGLE = 90;
  const ANGLE_RANGE = END_ANGLE - START_ANGLE;

  // 計算指針角度
  const needleAngle = useMemo(() => {
    const ratio = Math.min(Math.max(power / maxPower, 0), 1.2);
    return START_ANGLE + ratio * ANGLE_RANGE;
  }, [power, maxPower]);

  // SVG 中心點和半徑
  const cx = size / 2;
  const cy = size * 0.5;
  const radius = size * 0.38;

  // 極坐標轉笛卡爾坐標
  const polarToCartesian = (
    centerX: number,
    centerY: number,
    r: number,
    angleInDegrees: number
  ): { x: number; y: number } => {
    const angleInRadians = (angleInDegrees - 90) * (Math.PI / 180);
    return {
      x: centerX + r * Math.cos(angleInRadians),
      y: centerY + r * Math.sin(angleInRadians),
    };
  };

  // 計算弧形路徑
  const describeArc = (
    x: number,
    y: number,
    r: number,
    startAngle: number,
    endAngle: number
  ): string => {
    const start = polarToCartesian(x, y, r, endAngle);
    const end = polarToCartesian(x, y, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return ['M', start.x, start.y, 'A', r, r, 0, largeArcFlag, 0, end.x, end.y].join(' ');
  };

  // 計算指針端點
  const needleEnd = polarToCartesian(cx, cy, radius - 8, needleAngle);
  const needleBaseLeft = polarToCartesian(cx, cy, 4, needleAngle - 90);
  const needleBaseRight = polarToCartesian(cx, cy, 4, needleAngle + 90);

  // 格式化功率顯示
  const formatPower = (w: number): string => {
    if (w >= 1000) return `${(w / 1000).toFixed(1)}kW`;
    return `${w.toFixed(0)}W`;
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.6} viewBox={`0 0 ${size} ${size * 0.6}`}>
        {/* 背景弧形 */}
        <path
          d={describeArc(cx, cy, radius, START_ANGLE, END_ANGLE)}
          fill="none"
          stroke="#374151"
          strokeWidth="8"
          strokeLinecap="round"
        />

        {/* 進度弧形 */}
        <path
          d={describeArc(cx, cy, radius, START_ANGLE, Math.min(needleAngle, END_ANGLE))}
          fill="none"
          stroke={config.primary}
          strokeWidth="8"
          strokeLinecap="round"
        />

        {/* 指針 */}
        <polygon
          points={`${needleEnd.x},${needleEnd.y} ${needleBaseLeft.x},${needleBaseLeft.y} ${needleBaseRight.x},${needleBaseRight.y}`}
          fill={config.primary}
        />

        {/* 中心圓點 */}
        <circle cx={cx} cy={cy} r={5} fill={config.primary} />
        <circle cx={cx} cy={cy} r={2} fill="white" />

        {/* 功率數值 - 在環內下方 */}
        <text
          x={cx}
          y={cy - radius * 0.25}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-gray-900 fill-slate-200"
          fontSize={size * 0.11}
          fontWeight="bold"
        >
          {formatPower(power)}
        </text>
      </svg>

      {/* 相位標籤 */}
      <div
        className="px-5 py-1.5 rounded-full text-base font-bold"
        style={{ backgroundColor: config.light, color: config.primary }}
      >
        {config.label}
      </div>

      {/* 電壓/電流數值 */}
      <div className="mt-3 flex items-center gap-4 text-base text-slate-400">
        <span><span className="font-bold text-slate-200 text-lg">{voltage.toFixed(0)}</span> V</span>
        <span className="text-slate-300 text-slate-400">|</span>
        <span><span className="font-bold text-slate-200 text-lg">{current.toFixed(1)}</span> A</span>
      </div>
    </div>
  );
};

export default PhaseGauge;
